
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { isSupabaseConfigured } from '@/utils/supabase';
import { getCurrentUser, UserData, signOut } from '@/utils/authService';
import { useToast } from '@/hooks/use-toast';

type AuthContextType = {
  user: UserData | null;
  loading: boolean;
  logout: () => Promise<void>;
  setUser: (user: UserData | null) => void;
  isConfigured: boolean;
  refreshUser: () => Promise<void>;
  isEmailUnverified: boolean;
  isAwaitingApproval: boolean;
  isInPasswordResetFlow: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailUnverified, setIsEmailUnverified] = useState(false);
  const [isAwaitingApproval, setIsAwaitingApproval] = useState(false);
  const [isInPasswordResetFlow, setIsInPasswordResetFlow] = useState(false);
  const isConfigured = isSupabaseConfigured();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Add refreshUser function to fetch latest user data
  const refreshUser = async () => {
    if (!isConfigured) return;
    
    try {
      const userData = await getCurrentUser();
      console.log("Refreshed user data:", userData);
      setUser(userData);
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      if (import.meta.env.DEV) {
        console.warn('Supabase is not properly configured. Authentication is disabled.');
      }
      return;
    }

    console.log("Auth state check initialized");
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      
      // Check if this is a recovery session (password reset flow)
      const urlHash = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);
      const isRecoveryFlow = urlHash.includes('type=recovery') || urlParams.get('type') === 'recovery';
      const isOnResetPage = window.location.pathname === '/reset-password';
      
      if (event === 'SIGNED_IN' && session && isRecoveryFlow) {
        console.log("User signed in from password reset - entering recovery flow");
        setIsInPasswordResetFlow(true);
        // For recovery flow, skip normal user data checks and allow password reset
        setUser(null);
        setIsEmailUnverified(false);
        setIsAwaitingApproval(false);
        setLoading(false);
        return;
      } else if (event === 'PASSWORD_RECOVERY') {
        console.log("Password recovery event detected");
        setIsInPasswordResetFlow(true);
        setLoading(false);
        return;
      } else {
        setIsInPasswordResetFlow(false);
      }
      
      if (event === 'SIGNED_IN' && session) {
        setTimeout(async () => {
          try {
            // Check email verification status first
            if (!session.user.email_confirmed_at) {
              console.log("User signed in but email not verified");
              setIsEmailUnverified(true);
              setUser(null);
              setLoading(false);
              return;
            }

            const userData = await getCurrentUser();
            console.log("User data after sign in:", userData);
            
            // Check if user is verified but not approved
            if (userData && !userData.isApproved) {
              setIsAwaitingApproval(true);
              setUser(null);
            } else {
              setUser(userData);
              setIsAwaitingApproval(false);
            }
            setIsEmailUnverified(false);
          } catch (error) {
            console.error("Error getting user data:", error);
            setUser(null);
          } finally {
            setLoading(false);
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        console.log("User signed out");
        setUser(null);
        setIsEmailUnverified(false);
        setIsAwaitingApproval(false);
        setIsInPasswordResetFlow(false);
        setLoading(false);
      }
    });

    const checkUser = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          console.log("Existing session found");
          
          // Check email verification status
          if (!data.session.user.email_confirmed_at) {
            console.log("User has session but email not verified");
            setIsEmailUnverified(true);
            setUser(null);
            setLoading(false);
            return;
          }

          const userData = await getCurrentUser();
          console.log("Initial user data:", userData);
          
          // Check if user is verified but not approved
          if (userData && !userData.isApproved) {
            setIsAwaitingApproval(true);
            setUser(null);
          } else {
            setUser(userData);
            setIsAwaitingApproval(false);
          }
          setIsEmailUnverified(false);
        } else {
        console.log("No existing session");
        setUser(null);
        setIsEmailUnverified(false);
        setIsAwaitingApproval(false);
        setIsInPasswordResetFlow(false);
        }
      } catch (error) {
      console.error("Error checking user:", error);
      setUser(null);
      setIsEmailUnverified(false);
      setIsAwaitingApproval(false);
      setIsInPasswordResetFlow(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkUser();

    return () => {
      subscription.unsubscribe();
    };
  }, [isConfigured]);

  const logout = async () => {
    try {
      setLoading(true);
      await signOut();
      setUser(null);
      navigate('/');
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error) {
      console.error('Error logging out:', error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    logout,
    setUser,
    isConfigured,
    refreshUser,
    isEmailUnverified,
    isAwaitingApproval,
    isInPasswordResetFlow,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
